import createClient, {
  createFinalURL,
  createQuerySerializer,
  defaultPathSerializer,
  ParamsOption,
  RequestBodyOption,
  QuerySerializerOptions,
} from "openapi-fetch";
import {AccountId, ErrorResponse, paths} from "./types.ts";
import {HttpMethod, PathsWithMethod} from "openapi-typescript-helpers";
import {HTTPError} from "./error";
export {HTTPError, errorDescription} from "./error";

type ExternalPathPattern = `/external_api/${string}` | `/region/{region}/external_api/${string}`;

type ExternalPaths = {
  [K in keyof paths as K extends ExternalPathPattern ? K : never]: paths[K];
};
type InternalPaths = {
  [K in keyof paths as K extends ExternalPathPattern ? never : K]: paths[K];
};

async function fetchWithRetry(input: RequestInfo, init?: RequestInit | undefined): Promise<Response> {
  const request = new Request(input, init);

  for (let attempt = 1; ; ++attempt) {
    const innerRequest = request.clone();
    innerRequest.headers.set("attempt", attempt.toString());
    const response = await fetch(innerRequest);
    const retryAfterStr = response.headers.get("retry-after");
    if (response.status !== 503 || !retryAfterStr) {
      return response;
    }
    const retryAfter = parseInt(retryAfterStr);
    await abortableDelay(retryAfter * 1000, request.signal);
  }
}

function abortableDelay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason);
      return;
    }
    const abort = () => {
      clearTimeout(timer);
      reject(signal.reason);
    };
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", abort);
      resolve();
    }, ms);
    signal.addEventListener("abort", abort, {once: true});
  });
}

type RequestArgs<Spec> = Omit<ParamsOption<Spec>["params"], "header"> &
  RequestBodyOption<Spec> & {
    query?: object;
    path?: object;
    header?: {Authorization?: `Bearer ${string}` | `Basic ${string}`};
    cookie?: unknown;
    skipErrorHandler?: boolean;
    signal?: AbortSignal;
  };

type OptionalRegion<T> = T extends {path: infer P}
  ? // If path only has region as a required key, make path optional
    (keyof P extends "region"
      ? {path?: P extends {region: infer R} ? Omit<P, "region"> & {region?: R} : P}
      : {path: P extends {region: infer R} ? Omit<P, "region"> & {region?: R} : P}) &
      Omit<T, "path">
  : T;

type DecodeResponse<R> = R extends {content: infer C} ? (C extends {"application/json": infer J} ? J : never) : never;
type SuccessfulResponse<Spec> = Spec extends {responses: infer R} ? DecodeResponse<R[keyof R & number]> : never;

// Use comma separated array notation rather than the default repeated param style
const querySerializerOptions: QuerySerializerOptions = {array: {style: "form", explode: false}};

export class PlatformedClient {
  private internalClient: ReturnType<typeof createClient<InternalPaths>>;
  private externalClient: ReturnType<typeof createClient<ExternalPaths>>;

  // API information
  private release: string | undefined;

  // Account information for the "current" account when using the internal API
  private accountId: AccountId | null = null;
  private region: string | null = null;

  // Error handler
  private errorHandler: ((error: HTTPError) => void) | null = null;

  constructor(
    private baseUrl: string,
    release: string | undefined,
  ) {
    this.release = release;
    this.internalClient = createClient<InternalPaths>({
      baseUrl,
      fetch: fetchWithRetry,
      querySerializer: querySerializerOptions,
    });
    this.externalClient = createClient<ExternalPaths>({
      baseUrl,
      fetch: fetchWithRetry,
      querySerializer: querySerializerOptions,
      credentials: "include",
    });
  }

  setAccountId(accountId: AccountId | null) {
    this.accountId = accountId;
  }
  setRegion(region: string | null) {
    this.region = region;
  }
  setErrorHandler(handler: ((error: HTTPError) => void) | null) {
    this.errorHandler = handler;
  }

  async throwErrorForResponse(response: Response, error: ErrorResponse | undefined, skipErrorHandler?: boolean) {
    const release = response.headers.get("X-Release");
    // If we're out of date, reload the page
    if (release && this.release !== release) {
      window.location.reload();
    }
    if (!response.ok) {
      const err = await HTTPError.fromResponse(response, error);
      if (!skipErrorHandler) {
        this.errorHandler?.(err);
      }
      throw err;
    }
  }

  async internalRequest<const Method extends HttpMethod, const Path extends PathsWithMethod<InternalPaths, Method>>(
    method: Method,
    url: Path,
    init: OptionalRegion<RequestArgs<InternalPaths[Path][Method]>>,
  ): Promise<SuccessfulResponse<InternalPaths[Path][Method]>> {
    const {
      query = {},
      path = {},
      body,
      skipErrorHandler,
      signal,
      ...rest
    } = init as {query?: object; path?: object; body?: unknown; skipErrorHandler?: boolean; signal?: AbortSignal};

    const {...newQuery} = query;
    const {...newPath} = path;

    // Inject account_id query parameter. Skip for internal-dashboard
    // routes — those are Platformed-staff cross-account views that
    // accept their own `account_id` filter from the URL bar.
    const isInternalDashboardRoute =
      url.startsWith("/api/internal/") || url.startsWith("/region/{region}/api/internal/");
    if (this.accountId && !isInternalDashboardRoute) {
      // We override the type because the query parameter will just be ignored if the endpoint doesn't support it.
      (newQuery as {account_id?: AccountId}).account_id = this.accountId;
    }
    // Inject region into path parameters if needed
    if (url.startsWith("/region/{region}/") && !Object.hasOwn(newPath, "region")) {
      if (!this.region) {
        throw new Error("Region must be set before making region-scoped requests");
      }
      // We override the type because we checked dynamically that a region is needed.
      (newPath as {region?: string}).region = this.region;
    }
    const {response, data, error} = await this.internalClient.request(method, url, {
      params: {query: newQuery, path: newPath, ...rest},
      body,
      signal,
    } as never);

    await this.throwErrorForResponse(response, error as ErrorResponse | undefined, skipErrorHandler);

    return data as SuccessfulResponse<InternalPaths[Path][Method]>;
  }

  internalUrl<const Method extends HttpMethod, const Path extends PathsWithMethod<InternalPaths, Method>>(
    _method: Method,
    url: Path,
    init: Omit<
      OptionalRegion<RequestArgs<InternalPaths[Path][Method]>>,
      "body" | "header" | "cookie" | "skipErrorHandler" | "signal"
    >,
  ): string {
    const {query = {}, path = {}} = init as {query?: Record<string, unknown>; path?: Record<string, unknown>};

    const {...newQuery} = query;
    const {...newPath} = path;

    // Inject account_id query parameter
    if (this.accountId) {
      // We override the type because the query parameter will just be ignored if the endpoint doesn't support it.
      (newQuery as {account_id?: AccountId}).account_id = this.accountId;
    }
    // Inject region into path parameters if needed
    if (url.startsWith("/region/{region}/") && !Object.hasOwn(newPath, "region")) {
      if (!this.region) {
        throw new Error("Region must be set before making region-scoped requests");
      }
      // We override the type because we checked dynamically that a region is needed.
      (newPath as {region?: string}).region = this.region;
    }
    const finalUrl = createFinalURL(url, {
      baseUrl: this.baseUrl,
      params: {query: newQuery, path: newPath},
      querySerializer: createQuerySerializer(querySerializerOptions),
      pathSerializer: defaultPathSerializer,
    });
    return finalUrl;
  }

  async externalRequest<const Method extends HttpMethod, const Path extends PathsWithMethod<ExternalPaths, Method>>(
    method: Method,
    url: Path,
    init: RequestArgs<ExternalPaths[Path][Method]>,
  ): Promise<SuccessfulResponse<ExternalPaths[Path][Method]>> {
    const {body, signal, ...params} = init;
    const {response, data, error} = await this.externalClient.request(method, url, {params, body, signal} as never);

    await this.throwErrorForResponse(response, error as ErrorResponse | undefined, true);

    return data as SuccessfulResponse<ExternalPaths[Path][Method]>;
  }
  externalUrl<const Method extends HttpMethod, const Path extends PathsWithMethod<ExternalPaths, Method>>(
    _method: Method,
    url: Path,
    init: Omit<
      OptionalRegion<RequestArgs<ExternalPaths[Path][Method]>>,
      "body" | "header" | "cookie" | "skipErrorHandler" | "signal"
    >,
  ): string {
    const {query = {}, path = {}} = init as {query?: Record<string, unknown>; path?: Record<string, unknown>};

    const finalUrl = createFinalURL(url, {
      baseUrl: this.baseUrl,
      params: {query, path},
      querySerializer: createQuerySerializer(querySerializerOptions),
      pathSerializer: defaultPathSerializer,
    });
    return finalUrl;
  }
}
